import React, { useState, useEffect } from "react"
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MuiAlert from "../utils/muiAlerts"
import axios from "axios";

import { useModal } from "../contexts/modal-context";
import { backendOrigin } from "../utils/backendOrigin";
import { viewLocation } from "../utils/commonUtils";
import { invalidateCaches } from "../utils/mutationUtils";
import { hasMaxLength, hasUnsafeTextInObject, isValidPhoneNumber, sanitizeText } from "../utils/validationUtils";

const PatientModal = () => {

    const { modalShow, modalData, closeModal } = useModal()
    const { currentUserInfo, apiEndCallback, patientData = null } = modalData

    const [patientId, setPatientId] = useState(null)
    const [patientName, setPatientName] = useState("")
    const [contactNumber, setContactNumber] = useState("")
    const [patientNumber, setPatientNumber] = useState(0)
    const [age, setAge] = useState("")
    const [sex, setSex] = useState("male")
    const [patientAddress, setPatientAddress] = useState("")
    const [notes, setNotes] = useState("")
    const [mapCoordinates, setMapCoordinates] = useState({ latitude: "", longitude: "" })
    const [isGeolocationLoading, setIsGeolocationLoading] = useState(false)
    const [isSaveApiLoading, setIsSaveApiLoading] = useState(false)

    useEffect(() => {
        if (patientData) {
            setPatientId(patientData.id)
            setPatientName(patientData.patient_name)
            setContactNumber(patientData.contact_number)
            setPatientNumber(patientData.patient_number)
            setAge(patientData.age)
            setSex(patientData.sex)
            setPatientAddress(patientData.patient_address)
            setNotes(patientData.notes)
            setMapCoordinates(patientData.map_coordinates)
        }

        if (currentUserInfo && !patientData) {
            axios.post(`${backendOrigin}/get-patient-number`, { current_user_uid: currentUserInfo.uid, current_user_name: currentUserInfo.displayName }, { headers: { 'Content-Type': 'application/json' } })
                .then((res) => {
                    if (res.data.operation === "success") {
                        setPatientNumber(res.data.info)
                    }
                    else {
                        MuiAlert.fire('Oops!', res.data.message, 'error');
                    }
                })
                .catch((err) => {
                    console.error(err)
                    MuiAlert.fire('Error!!', err.message, 'error');
                })
        }
    }, [patientData, currentUserInfo])


    const configurePatient = () => {
        const safePatientName = sanitizeText(patientName)
        const safeContactNumber = sanitizeText(contactNumber)
        const safeAge = sanitizeText(age)
        const safePatientAddress = sanitizeText(patientAddress)
        const safeNotes = sanitizeText(notes)

        if (!patientName) {
            MuiAlert.fire('Oops!!', 'Patient name cannot be empty', 'warning');
            return false
        }
        if (!contactNumber) {
            MuiAlert.fire('Oops!!', 'Contact number cannot be empty', 'warning');
            return false
        }
        if (!patientNumber) {
            MuiAlert.fire('Oops!!', 'Patient number invalid', 'warning');
            return false
        }
        if (!age) {
            MuiAlert.fire('Oops!!', 'Age cannot be empty', 'warning');
            return false
        }
        if (!sex) {
            MuiAlert.fire('Oops!!', 'Sex cannot be empty', 'warning');
            return false
        }
        if (!patientAddress) {
            MuiAlert.fire('Oops!!', 'Patient address cannot be empty', 'warning');
            return false
        }
        if (!isValidPhoneNumber(safeContactNumber)) {
            MuiAlert.fire('Oops!!', 'Enter a valid 10-digit contact number', 'warning');
            return false
        }
        if (!/^\d{1,3}$/.test(safeAge)) {
            MuiAlert.fire('Oops!!', 'Enter a valid age', 'warning');
            return false
        }
        if (!hasMaxLength(safePatientName, 120) || !hasMaxLength(safePatientAddress, 300) || !hasMaxLength(safeNotes, 500)) {
            MuiAlert.fire('Oops!!', 'One or more fields exceed allowed length', 'warning');
            return false
        }
        if (hasUnsafeTextInObject([safePatientName, safeContactNumber, safeAge, safePatientAddress, safeNotes])) {
            MuiAlert.fire('Oops!!', 'Invalid characters detected in form fields', 'warning');
            return false
        }

        let data = {
            patient_id: patientId,

            patient_name: safePatientName,
            contact_number: safeContactNumber,
            patient_number: patientNumber,
            age: safeAge,
            sex: sex,
            patient_address: safePatientAddress,
            notes: safeNotes,
            map_coordinates: mapCoordinates,

            current_user_uid: currentUserInfo.uid,
            current_user_name: currentUserInfo.displayName
        }

        setIsSaveApiLoading(true)
        axios.post(`${backendOrigin}/configure-patient`, data, { headers: { 'Content-Type': 'application/json' } })
            .then(async (res) => {
                setIsSaveApiLoading(false)

                if (res.data.operation === "success") {
                    await invalidateCaches(['patients'])
                    MuiAlert.fire('Success!', res.data.message, 'success');
                    handlePatientModalClose()

                    apiEndCallback(res.data.info)
                }
                else {
                    MuiAlert.fire('Oops!', res.data.message, 'error');
                }
            })
            .catch((err) => {
                console.error(err)
                MuiAlert.fire('Error!!', err.message, 'error');
            })
    }

    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            MuiAlert.fire('Oops!!', 'Geolocation is not supported by your browser', 'danger');
        } else {
            setIsGeolocationLoading(true)

            navigator.geolocation.getCurrentPosition((position) => {
                setIsGeolocationLoading(false)
                const latitude = position.coords.latitude;
                const longitude = position.coords.longitude;

                setMapCoordinates({ latitude, longitude })
            }, () => {
                setIsGeolocationLoading(false)
                MuiAlert.fire('Unable to retrieve your location', 'Please allow location access from site settings', 'warning');
            }, {
                enableHighAccuracy: true,
                maximumAge: 30000,
                timeout: 27000,
            });
        }
    }

    const handlePatientModalClose = () => {
        setPatientId(null)
        setPatientName("")
        setContactNumber("")
        setPatientNumber(0)
        setAge("")
        setSex("male")
        setPatientAddress("")
        setNotes("")
        setMapCoordinates({ latitude: "", longitude: "" })

        closeModal()
    }

    return (
        <Dialog open={modalShow} onClose={() => { handlePatientModalClose() }} maxWidth="md" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{patientId ? "Update Patient" : "Add Patient"}</Typography>
                <IconButton onClick={() => { handlePatientModalClose() }} size="small" sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <CloseRoundedIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2.5} sx={{ pt: 0.5 }}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Fill patient basics first. Location and notes are optional.
                    </Alert>

                    <Grid container spacing={2}>
                        <Grid size={{ md: 6 }}>
                            <TextField
                                fullWidth
                                required
                                id="patientName"
                                label="Patient Name"
                                placeholder="Enter name"
                                value={patientName}
                                onChange={(e) => { setPatientName(e.target.value) }}
                            />
                        </Grid>
                        <Grid size={{ md: 6 }}>
                            <TextField
                                fullWidth
                                required
                                id="contactNumber"
                                label="Contact Number"
                                placeholder="Enter contact number"
                                value={contactNumber}
                                onChange={(e) => { setContactNumber(e.target.value) }}
                            />
                        </Grid>

                        <Grid size={{ md: 6 }}>
                            <TextField
                                fullWidth
                                required
                                id="patientNumber"
                                type="number"
                                label="Patient No."
                                value={patientNumber.toString()}
                                onChange={(e) => { setPatientNumber(e.target.value === "" ? 0 : parseInt(e.target.value)) }}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">PAT</InputAdornment>,
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 6 }}>
                            <TextField
                                fullWidth
                                required
                                id="age"
                                label="Age"
                                placeholder="Enter age"
                                value={age}
                                onChange={(e) => { setAge(e.target.value) }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Sex</Typography>
                            <ToggleButtonGroup
                                exclusive
                                fullWidth
                                value={sex}
                                onChange={(_, value) => {
                                    if (value) setSex(value)
                                }}
                            >
                                <ToggleButton value="male">Male</ToggleButton>
                                <ToggleButton value="female">Female</ToggleButton>
                                <ToggleButton value="others">Others</ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>

                        <Grid size={{ md: 6 }}>
                            <TextField
                                fullWidth
                                required
                                multiline
                                minRows={3}
                                id="patientAddress"
                                label="Patient Address"
                                placeholder="Enter address"
                                value={patientAddress}
                                onChange={(e) => { setPatientAddress(e.target.value) }}
                            />
                        </Grid>
                        <Grid size={{ md: 6 }}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                id="notes"
                                label="Notes"
                                placeholder="Enter notes"
                                value={notes}
                                onChange={(e) => { setNotes(e.target.value) }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}><Divider /></Grid>

                        <Grid size={{ md: 6 }}>
                            <TextField
                                fullWidth
                                type="number"
                                id="latitude"
                                label="Latitude"
                                value={mapCoordinates.latitude}
                                onChange={(e) => { setMapCoordinates({ ...mapCoordinates, latitude: e.target.value }) }}
                            />
                        </Grid>
                        <Grid size={{ md: 6 }}>
                            <TextField
                                fullWidth
                                type="number"
                                id="longitude"
                                label="Longitude"
                                value={mapCoordinates.longitude}
                                onChange={(e) => { setMapCoordinates({ ...mapCoordinates, longitude: e.target.value }) }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    color="info"
                                    onClick={() => { getCurrentLocation() }}
                                    disabled={isGeolocationLoading}
                                    startIcon={isGeolocationLoading ? <CircularProgress size={16} /> : undefined}
                                >
                                    {isGeolocationLoading ? 'Detecting...' : 'Get Current Location'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => { viewLocation(mapCoordinates) }}
                                    disabled={!mapCoordinates.latitude || !mapCoordinates.longitude}
                                >
                                    View Location
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="success"
                    disabled={isSaveApiLoading}
                    onClick={() => { !isSaveApiLoading && configurePatient() }}
                    startIcon={isSaveApiLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {isSaveApiLoading ? 'Saving...' : 'Submit'}
                </Button>
                <Button variant="outlined" onClick={() => { handlePatientModalClose() }}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}

export default PatientModal
